import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, RefreshCw, Phone, MessageSquare, Calendar, Mail, Users, CheckCircle2, Clock,
  XCircle, Loader2, Search, Trash2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { ASSETS, SITE, whatsappLink } from "../lib/constants";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_COLORS = {
  new: "bg-amber-50 text-amber-700 border-amber-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-teal-50 text-teal-700 border-teal-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUSES = ["new", "contacted", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const adminEmail = localStorage.getItem("lotus_admin_email");

  useEffect(() => {
    if (!localStorage.getItem("lotus_admin_token")) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, contactRes, statsRes] = await Promise.all([
        api.get("/appointments"),
        api.get("/contacts"),
        api.get("/admin/stats"),
      ]);
      setAppointments(apptRes.data);
      setContacts(contactRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("lotus_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not load data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("lotus_admin_token");
    localStorage.removeItem("lotus_admin_email");
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}`);
      // refresh stats
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      toast.error("Could not update status");
    }
  };

  const deleteAppointment = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      toast.success("Deleted");
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      toast.error("Could not delete");
    }
  };

  const filteredAppointments = appointments.filter((a) => {
    const matchesSearch =
      !search ||
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.phone.includes(search) ||
      (a.service || "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <div className="w-11 h-11 flex items-center justify-center">
              <img src={ASSETS.logo} alt="" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-slate-900">{SITE.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-teal-600">Admin Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-slate-100 text-sm text-slate-600"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <div className="hidden md:block text-xs text-slate-500">{adminEmail}</div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <h1 className="font-heading text-3xl font-light tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Manage appointments, leads & contact requests.</p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Appointments", value: stats?.total_appointments ?? "—", icon: Calendar, color: "text-teal-600 bg-teal-50" },
            { label: "New", value: stats?.new_appointments ?? "—", icon: Clock, color: "text-amber-600 bg-amber-50" },
            { label: "Confirmed", value: stats?.confirmed ?? "—", icon: CheckCircle2, color: "text-blue-600 bg-blue-50" },
            { label: "Completed", value: stats?.completed ?? "—", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
            { label: "Contact Requests", value: stats?.total_contacts ?? "—", icon: Mail, color: "text-slate-600 bg-slate-100" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`stat-card-${i}`}>
              <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 font-heading text-3xl font-light text-slate-900">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="mt-10">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-11">
            <TabsTrigger value="appointments" className="rounded-full px-5 data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-appointments">
              Appointments ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full px-5 data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-contacts">
              Contact Requests ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="mt-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, phone, service…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                  data-testid="admin-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-44 h-10 rounded-xl border-slate-200" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="mt-4 bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {loading ? (
                <div className="p-12 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : filteredAppointments.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Users className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="mt-3">No appointments found.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="appointments-table">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-3 text-left font-medium">Patient</th>
                        <th className="px-5 py-3 text-left font-medium">Service</th>
                        <th className="px-5 py-3 text-left font-medium">Preferred</th>
                        <th className="px-5 py-3 text-left font-medium">Created</th>
                        <th className="px-5 py-3 text-left font-medium">Status</th>
                        <th className="px-5 py-3 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAppointments.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`appointment-row-${a.id}`}>
                          <td className="px-5 py-4">
                            <div className="font-medium text-slate-900">{a.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                              <a href={`tel:${a.phone}`} className="hover:text-teal-700 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {a.phone}
                              </a>
                              <a
                                href={whatsappLink(`Hi ${a.name}, this is Lotus Family Dental — about your appointment.`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-teal-700 flex items-center gap-1"
                              >
                                <MessageSquare className="w-3 h-3" /> WhatsApp
                              </a>
                            </div>
                            {a.email && (
                              <div className="text-xs text-slate-500 mt-0.5">{a.email}</div>
                            )}
                            {a.message && (
                              <div className="text-xs text-slate-500 mt-1 italic">"{a.message}"</div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-slate-700">{a.service || "—"}</td>
                          <td className="px-5 py-4 text-slate-600">
                            {a.preferred_date || "—"}
                            {a.preferred_time && (
                              <div className="text-xs text-slate-500">{a.preferred_time}</div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-slate-600 text-xs">{formatDate(a.created_at)}</td>
                          <td className="px-5 py-4">
                            <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                              <SelectTrigger
                                className={`h-8 rounded-full text-xs px-3 border ${STATUS_COLORS[a.status] || ""}`}
                                data-testid={`status-select-${a.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                                  data-testid={`delete-button-${a.id}`}
                                  aria-label="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this appointment?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the appointment record for {a.name}. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteAppointment(a.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {loading ? (
                <div className="p-12 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Mail className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="mt-3">No contact requests yet.</div>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="contacts-list">
                  {contacts.map((c) => (
                    <li key={c.id} className="p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                            {c.phone && (
                              <a href={`tel:${c.phone}`} className="hover:text-teal-700 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {c.phone}
                              </a>
                            )}
                            {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                            <span>{formatDate(c.created_at)}</span>
                          </div>
                          <p className="mt-2 text-sm text-slate-700">{c.message}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
